import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { VenueFromBackend } from '@shared/form-models-interfaces';
import { IGroup } from '@shared/interfaces/groups/group.interface';
import { matchesSearch, toSearchQuery } from '@shared/utils/search-text';

export interface AddVenuesDialogData {
  group: IGroup;
  candidates: VenueFromBackend[];
  childrenNameSingular: string;
  childrenNamePlural: string;
}

export const addVenuesDialogConfig: MatDialogConfig = {
  width: '680px',
  disableClose: true,
  panelClass: 'add-venues-dialog-panel',
  autoFocus: 'input[data-testid="input-bulk-search"]',
};

@Component({
  selector: 'app-add-venues-dialog',
  templateUrl: './add-venues-dialog.component.html',
  styleUrls: ['./add-venues-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    MatButton,
    MatCheckbox,
    MatDialogClose,
    MatFormField,
    MatPrefix,
    MatIcon,
    MatInput,
    TranslatePipe,
  ],
})
export class AddVenuesDialogComponent {
  readonly data = inject<AddVenuesDialogData>(MAT_DIALOG_DATA);

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly filteredCandidates = computed<VenueFromBackend[]>(() => {
    const query = toSearchQuery(this.searchQuery());

    if (!query) {
      return this.data.candidates;
    }

    return this.data.candidates.filter(venue => matchesSearch(venue.brandName, query));
  });
  /** Venues with no group memberships — the primary candidates to organise. */
  readonly ungrouped = computed<VenueFromBackend[]>(() =>
    this.filteredCandidates().filter(v => !v.venueGroups?.length)
  );
  /** Venues already in at least one other group. */
  readonly inGroups = computed<VenueFromBackend[]>(() =>
    this.filteredCandidates().filter(v => !!v.venueGroups?.length)
  );
  readonly selectedUuids = signal<ReadonlySet<string>>(new Set());
  readonly selectedCount = computed(() => this.selectedUuids().size);
  readonly hasSearchQuery = computed(() => !!this.searchQuery().trim());

  private readonly mdDialogRef =
    inject<MatDialogRef<AddVenuesDialogComponent, VenueFromBackend[]>>(MatDialogRef);
  private readonly searchQuery = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  isSelected(venue: VenueFromBackend): boolean {
    return !!venue.uuid && this.selectedUuids().has(venue.uuid);
  }

  toggle(venue: VenueFromBackend) {
    if (!venue.uuid) {
      return;
    }

    const venueUuid = venue.uuid;

    this.selectedUuids.update(selected => {
      const next = new Set(selected);

      if (next.has(venueUuid)) {
        next.delete(venueUuid);
      } else {
        next.add(venueUuid);
      }

      return next;
    });
  }

  selectAllFiltered() {
    this.selectedUuids.update(selected => {
      const next = new Set(selected);

      this.filteredCandidates().forEach(venue => {
        if (venue.uuid) {
          next.add(venue.uuid);
        }
      });

      return next;
    });
  }

  groupNamesOf(venue: VenueFromBackend): string[] {
    return (venue.venueGroups ?? []).map(group => group.name);
  }

  confirm() {
    this.mdDialogRef.close(this.data.candidates.filter(venue => this.isSelected(venue)));
  }
}
